import { useEffect, useState } from 'react';
import { ResizeObserver } from 'resize-observer';
import _ from 'lodash';
import { usePersistedValue } from '../react-hooks';

export type DimensionsCallback<R = void> = (width: number, height: number) => R;

export const useElementDimensions =
  (elementId: string, dimensionsCallback: DimensionsCallback, debounceDuration: number) => {
    useEffect(() => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`[useElementDimensions] could not find element with id '${elementId}'`); // eslint-disable-line no-console
        return () => {};
      }

      const callback = (entries) => entries
        .forEach(({ contentRect }) => dimensionsCallback(contentRect.width, contentRect.height));
      const observer = new ResizeObserver(
        debounceDuration ? _.debounce(callback, debounceDuration) : callback,
      );
      observer.observe(element);
      return () => observer.disconnect();
    }, [elementId, dimensionsCallback, debounceDuration]);
  };

const viewportSize: DimensionsCallback<[number, number]> = (width, height) => [width, height];

export const useViewport = <S = [width: number, height: number]>(
  getValue: DimensionsCallback<S> = viewportSize as any, debounceDuration?: number,
): S => {
  const [value, setValue] = useState<S>(() => getValue(window.innerWidth, window.innerHeight));
  const persistedValue = usePersistedValue(value);

  useEffect(() => {
    const update = () => {
      const currentValue = getValue(window.innerWidth, window.innerHeight);
      if (_.isEqual(currentValue, persistedValue.get())) return;
      persistedValue.set(currentValue);
      setValue(currentValue);
    };
    const debounced = debounceDuration ? _.debounce(update, debounceDuration) : undefined;
    const callback = debounced ?? update;
    window.addEventListener('resize', callback);
    return () => {
      window.removeEventListener('resize', callback);
      debounced?.cancel();
    };
  }, []);

  return value;
};
